import { ThemeProvider } from "@mui/material/styles";
import { Route, Routes, Navigate } from "react-router-dom";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React, { Suspense } from "react";

import { useAuthContext } from "context/AuthContext";
import { AuthLayout, Layout } from "components/Layout";
import { theme } from "config/theme";
import { FallbackSpinner } from "components/FallbackSpinner";

const ProtectedRoute = ({ redirectPath, children }) => {
  const { isLoggedIn } = useAuthContext();
  if (!isLoggedIn) {
    return <Navigate to={redirectPath} replace />;
  }
  return children;
};

function lazyRetry(importFn) {
  return new Promise((resolve, reject) => {
    importFn()
      .then(resolve)
      .catch((error) => {
        // Only reload once
        if (!sessionStorage.getItem("retry")) {
          sessionStorage.setItem("retry", "true");
          window.location.reload();
        } else {
          reject(error);
        }
      });
  });
}

function Loadable(Component) {
  return (props) => {
    return (
      <Suspense fallback={<FallbackSpinner />}>
        <Component {...props} />
      </Suspense>
    );
  };
}

function lazy(load) {
  return React.lazy(() => lazyRetry(() => load()));
}

const Login = Loadable(lazy(() => import("pages/Login")));
const Collaborate = Loadable(lazy(() => import("pages/Collaborate")));
const Transactions = Loadable(lazy(() => import("pages/Transactions")));
const Dashboard = Loadable(lazy(() => import("pages/Dashboard/Dashboard")));
const Businesses = Loadable(lazy(() => import("pages/Businesses")));
const Activity = Loadable(lazy(() => import("pages/Activity")));
const BusinessTransactions = Loadable(
  lazy(() => import("pages/Businesses/BusinessTransactions")),
);
const BusinessTransactionWithdrawalDetails = Loadable(
  lazy(() => import("pages/Businesses/BusinessTransactionWithdrawalDetails")),
);
const Team = Loadable(lazy(() => import("pages/Administration/Team")));
const Roles = Loadable(lazy(() => import("pages/Administration/Roles")));
const Settings = Loadable(lazy(() => import("pages/Settings")));
const ForgotPassword = Loadable(lazy(() => import("pages/ForgotPassword")));
const ResetPassword = Loadable(
  lazy(() => import("pages/ResetPassword/ResetPassword")),
);
const VerifyOtp = Loadable(lazy(() => import("pages/VerifyOtp/VerifyOtp")));
const TransactionDetails = Loadable(
  lazy(() => import("pages/Transactions/TransactionDetails")),
);
const Watchlist = Loadable(lazy(() => import("pages/Watchlist")));

function App() {
  return (
    <ThemeProvider theme={theme}>
      <ReactQueryDevtools initialIsOpen />

      <Routes>
        <Route
          element={
            <ProtectedRoute redirectPath="/login">
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index path="/" element={<Dashboard />} />
          <Route path="/transactions">
            <Route index={true} element={<Transactions />} />
            <Route path=":id" element={<TransactionDetails />} />
          </Route>
          <Route path="/businesses">
            <Route index={true} element={<Businesses />} />
            <Route
              path=":businessId/transactions"
              element={<BusinessTransactions />}
            />
            <Route
              path=":businessId/transactions/:transactionId"
              element={<BusinessTransactionWithdrawalDetails />}
            />
          </Route>
          <Route path="/watchlist" element={<Watchlist />} />
          <Route path="/administration">
            <Route path="role-privileges" element={<Roles />} />
            <Route path="team-mate" element={<Team />} />
          </Route>
          <Route path="/activity-log" element={<Activity />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/collaborate/:id" element={<Collaborate />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-otp" element={<VerifyOtp />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Route>
      </Routes>
    </ThemeProvider>
  );
}

export default App;
